import { widgetService } from '@api/services/widgetService';
import { useEffect, useRef, useState } from 'react';
import { createAudioBufferFromLPCM } from '@ai/common/src/utils/createAudioBufferFromLPCM';
import FormFieldWrapper from '@ai/common/src/sections/expert-configuration/FormFieldWrapper';
import { useTranslation } from 'react-i18next';
import { Button, Collapse, Fab, Stack, TextField } from '@mui/material';
import { FormikProps } from 'formik';
import { ttsTextPreprocessor } from '@ai/common/src/utils/ttsTextPreprocessor';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { TARGET_SAMPLE_RATE } from '@ai/widget/src/stores/audioStreamStore';
import { ExpertForm } from '@ai/common/src/types/expert';

const MAX_TEXT_LENGTH = 1000;

export function SoundTest({ form }: { form: FormikProps<ExpertForm> }) {
  const [testText, setTestText] = useState('');
  const [audioState, setAudioState] = useState<'idle' | 'playing' | 'suspended' | 'loading'>(
    'idle'
  );

  const audioSource = useRef<AudioBufferSourceNode | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const audioBuffer = useRef<AudioBuffer | null>(null);

  const { t } = useTranslation();
  const {
    values: {
      config: { voice, emotion, speed, case_sensitive_replacements, case_insensitive_replacements },
    },
  } = form;

  const synthesizeText = async () => {
    stopAudio();

    setAudioState('loading');

    const processedText = ttsTextPreprocessor(
      testText,
      case_sensitive_replacements,
      case_insensitive_replacements,
      voice
    );

    const { data } = await widgetService.synthesize({
      text: processedText,
      voice: voice,
      emotion: emotion,
      speed: speed,
      sampleRate: TARGET_SAMPLE_RATE,
    });

    const buffer = await data.arrayBuffer();

    const context = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    audioContext.current = context;

    audioBuffer.current = createAudioBufferFromLPCM({
      arrayBuffer: buffer,
      numChannels: 1,
      sampleRate: TARGET_SAMPLE_RATE,
      context: context,
    });

    createAndStartAudioSource();
  };

  const createAndStartAudioSource = () => {
    if (!audioContext.current) return;

    const source = audioContext.current.createBufferSource();
    source.buffer = audioBuffer.current;
    source.connect(audioContext.current.destination);
    source.onended = () => {
      pauseAudio();
      audioSource.current = null;
    };

    audioSource.current = source;
    source.start();
    setAudioState('playing');
  };

  const pauseAudio = () => {
    if (audioContext.current && audioContext.current?.state === 'running') {
      audioContext.current?.suspend().then(() => setAudioState('suspended'));
    }
  };

  const resumeAudio = () => {
    if (audioContext.current?.state !== 'suspended') return;

    if (audioSource.current) {
      audioContext.current.resume().then(() => setAudioState('playing'));
    } else {
      if (audioBuffer.current) {
        audioContext.current.resume().then(createAndStartAudioSource);
      }
    }
  };

  const stopAudio = () => {
    audioSource.current?.stop();
    audioSource.current = null;

    audioContext.current?.close();
    audioContext.current = null;

    setAudioState('idle');
  };

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  return (
    <FormFieldWrapper
      title={t('expertsList.soundTest')}
      description={t('expertsList.soundTestDescr')}
      fieldComponent={
        <Stack gap={2}>
          <TextField
            value={testText}
            onChange={(e) => {
              setTestText(e.target.value);
              setAudioState('idle');
            }}
            placeholder={t('expertsList.textForSynthesis')}
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            slotProps={{
              htmlInput: { maxLength: MAX_TEXT_LENGTH },
              input: { sx: { background: 'white' } },
              formHelperText: { sx: { textAlign: 'end' } },
            }}
            helperText={
              testText.length >= MAX_TEXT_LENGTH * 0.8
                ? `${testText.length}/${MAX_TEXT_LENGTH}`
                : undefined
            }
          />

          <Stack direction={'row'} gap={1}>
            <Button
              variant="contained"
              sx={{ alignSelf: 'start', minWidth: 200 }}
              disabled={!testText.trim()}
              onClick={synthesizeText}
              loading={audioState === 'loading'}
              loadingPosition="start"
            >
              {t('expertsList.listen')}
            </Button>

            <Collapse in={audioState !== 'idle'} orientation="horizontal">
              <Fab
                size="small"
                onClick={audioState === 'playing' ? pauseAudio : resumeAudio}
                color="primary"
                disabled={audioState === 'loading'}
              >
                {audioState === 'playing' ? <PauseIcon /> : <PlayArrowIcon />}
              </Fab>
            </Collapse>
          </Stack>
        </Stack>
      }
    />
  );
}
